import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateTime, Duration } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Case } from '../../../../../../_base-shared/models/Case/Case';
import { CaseCreditor, CaseCreditorPivot } from '../../../../../../_base-shared/models/Case/CaseCreditor';
import { CaseCreditorListFilter } from '../../../../../../_base-shared/models/Case/CaseCreditorListFilter';
import { Creditor } from '../../../../../../_base-shared/models/Entity/Creditor';
import { PaymentPlanType } from '../../../../../../_base-shared/models/Product/PaymentPlanType';
import { CaseCreditorStatus } from '../../../../../../_base-shared/models/Status/CaseCreditorStatus';
import { User } from '../../../../../../_base-shared/models/User/User';
import { environment } from '../../../../environments/environment';
import {
  ChangeCreditorStatusComponent
} from '../../../_shared/components/change-creditor-status/change-creditor-status.component';
import { VerifySignatureComponent } from '../../../_shared/components/verify-signature/verify-signature.component';
import { MainBaseApiService } from '../../../_shared/services/main-base-api.service';
import { MainGlobalEventService } from '../../../_shared/services/main-global-event.service';
import { MiscConfigService } from '../../config/misc-config/misc-config.service';
import { CreditorService } from '../../creditor/creditor.service';
import { CaseCreditorStatusService } from '../../status/case-creditor/status-list/case-creditor-status.service';
import {
  AdditionalInputComponent
} from '../case-detail/case-creditor-list/additional-input/additional-input.component';
import {
  CalculationModalComponent
} from '../case-detail/case-creditor-list/calculation-modal/calculation-modal.component';
import {
  CaseCreditorInfoComponent
} from '../case-detail/case-creditor-list/creditor-dialog/case-creditor-info.component';
import {
  CasePaymentPlanGeneratorComponent
} from '../case-detail/case-payment/case-payment-plan-generator/case-payment-plan-generator.component';
import { CaseEditorPrefillComponent } from '../case-editor-prefill/case-editor-prefill.component';
import { CreditorCasesListFiltersComponent } from './creditor-cases-list-fitlers/creditor-cases-list-filters.component';
import { CaseCreditorService } from '../case-creditor.service';

@Component({
  standalone: false,
  selector:    'app-creditor-cases-v2',
  templateUrl: './creditor-cases-v2.component.html',
  styleUrls:   ['./creditor-cases-v2.component.scss']
})
export class CreditorCasesV2Component implements OnInit, AfterViewInit, OnDestroy {
  // Wrapper
  @ViewChild(CreditorCasesListFiltersComponent) creditorCasesListFiltersComponent: CreditorCasesListFiltersComponent;
  public onlyRelated: boolean;
  // End of wrapper

  public authUser: User;
  public isLoadingExport                          = 0;
  public filtersReady                             = false;
  public caseCreditorListFilter: CaseCreditorListFilter;
  // Datatable
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  public isLoadingCases                           = 0;
  public initialSelection                         = [];
  public allowMultiSelect                         = true;
  public selection                                = new SelectionModel(this.allowMultiSelect, this.initialSelection);
  public pageSelected                             = false;
  public displayedColumns: Array<string>;
  public totalResults: number;
  public totalPages: number;
  public dataSource: MatTableDataSource<CaseCreditor>;
  public defaultPaginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public paginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public defaultSort: { direction: 'asc' | 'desc', active: 'created_at' };
  public storageUrl                               = environment.STORAGE_URL + '/';
  public math                                     = Math;
  public debtsSettledAmount: number;
  public isLoading                                = 0;
  // End Datatable
  private initialFetchComplete: boolean;
  private subscriptions: Array<Subscription>      = [];
  public caseCreditorStatuses: Array<CaseCreditorStatus>;
  public isPublicLoading                          = 0;
  public isPublicLoadingId: string[]              = [];
  public isLoadingIds: Array<number>              = [];
  public paymentPlanTypes: Array<PaymentPlanType> = [];
  public filters: any;
  public canSendExtrajudicial                     = [1, 27, 36, 747150, 751600, 787448, 843623];
  public allowedUsers                             = [1, 27, 36, 751600, 752733];

  constructor(private route: ActivatedRoute,
              private fb: UntypedFormBuilder,
              private toastr: ToastrService,
              public dialog: MatDialog,
              private translate: TranslateService,
              public globalEventsService: MainGlobalEventService,
              private caseCreditorService: CaseCreditorService,
              private configService: MiscConfigService,
              private caseCreditorStatusService: CaseCreditorStatusService,
              private creditorService: CreditorService) {
  }

  ngOnInit(): void {
    this.subscriptions.push(this.globalEventsService.authUser$.subscribe(user => this.authUser = user));
    this.displayedColumns       = this.getTableColumns();
    this.onlyRelated            = this.route.snapshot.data.hasOwnProperty('onlyRelated') ?
      this.route.snapshot.data.onlyRelated :
      null;
    this.defaultPaginatorConfig = {pageIndex: 0, pageSize: 20, length: 1};
    this.defaultSort            = {direction: 'desc', active: 'created_at'};
    this.paginatorConfig        = this.defaultPaginatorConfig;
    this.fetchCaseCreditorStatuses();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public newCasePrefill(): void {
    this.dialog.open(CaseEditorPrefillComponent, {
      width:     '50%',
      minHeight: '230px'
    });
  }

  public clearFilters(): void {
    this.creditorCasesListFiltersComponent.clearFilters();
    this.applyFilters();
  }

  public handleFiltersState($event: boolean): void {
    this.filtersReady = $event;
  }

  public handleFilters($event: CaseCreditorListFilter): void {
    this.caseCreditorListFilter = $event;
    if ( ! this.initialFetchComplete) {
      this.applyFilters();
      this.initialFetchComplete = true;
    }
  }

  private resetPagination(): void {
    this.paginatorConfig                 = this.defaultPaginatorConfig;
    this.caseCreditorListFilter.per_page = this.paginatorConfig.pageSize;
    this.caseCreditorListFilter.page     = this.paginatorConfig.pageIndex;
  }

  private resetSort(): void {
    this.caseCreditorListFilter.sort_by = this.defaultSort.active;
    this.caseCreditorListFilter.order   = this.defaultSort.direction;
  }

  private fetchCaseCreditors(statusId = null): void {
    this.clearSelection();  //  Reset global select
    this.caseCreditorListFilter.start_date = this.caseCreditorListFilter.start_date && this.caseCreditorListFilter.start_date instanceof Date ?
      this.caseCreditorListFilter.start_date.toISOString() :
      this.caseCreditorListFilter.start_date;
    this.caseCreditorListFilter.end_date   = this.caseCreditorListFilter.end_date && this.caseCreditorListFilter.end_date instanceof Date ?
      this.caseCreditorListFilter.end_date.toISOString() :
      this.caseCreditorListFilter.end_date;

    const relations = ['case', 'creditor', 'case.payment_status', 'case.status', 'case_creditor_status', 'product', 'case.payment_plans', 'case.fees'
      , 'latest_case_creditor_status_log', 'case.debt_plan_fees'];

    const requestData = MainBaseApiService.convertFiltersForRequest(
      {...this.caseCreditorListFilter, with: relations, with_count: ['case.unsecured_case_creditors']}, 'get'
    );

    this.dataSource = new MatTableDataSource<CaseCreditor>([]);
    this.isLoadingCases++;
    this.subscriptions.push(
      this.caseCreditorService.index(requestData).pipe(finalize(() => this.isLoadingCases--)).subscribe(
        result => {
          this.clearSelection();

          result.data = result.data.map(caseCreditor => {
            const totalContribution = caseCreditor.case.debt_plan_fees.reduce((sum, item) => {
              return sum + (item.amount_paid || 0);
            }, 0);

            return {
              ...caseCreditor,
              discount:          caseCreditor.initial_balance - caseCreditor.negotiated_amount,
              totalContribution,
            };
          });


          this.dataSource = new MatTableDataSource<CaseCreditor>(result.data);

          this.dataSource.sort        = this.sort;
          this.paginatorConfig.length = result.meta.total;
          this.totalResults           = result.meta.total;
          this.totalPages             = result.meta.last_page;
        },
        err => console.error(err)
      )
    );
  }

  private mapLatestContactHistories(data, keyName) {
    data.map(obj => {
      if (obj.notification_histories?.length) {
        const notificationsReceived = obj.notification_histories.filter(notification => {
          return notification.notifiable_type === keyName;
        });

        if (notificationsReceived.length) {
          if (keyName === 'history_call') {
            const successfulCalls = this.getSuccessfulCalls(notificationsReceived);

            if (successfulCalls.length) {
              const latestSuccessfulCall  = this.getLatestNotification(successfulCalls);
              obj.history_successful_call = this.formatDateForDisplay(latestSuccessfulCall.contacted_at);
              obj.agent                   = latestSuccessfulCall?.staff?.first_name + ' ' +
                latestSuccessfulCall?.staff?.last_name;
            }
          }

          const latestNotification = this.getLatestNotification(notificationsReceived);

          if (keyName === 'history_call' && typeof latestNotification.notifiable !== 'undefined') {
            obj.call_duration = latestNotification.notifiable.call_duration;
          }
          // console.log(latestNotification);
          obj[keyName] = this.formatDateForDisplay(latestNotification.contacted_at);
          return obj;
        }
      }
    });
  }

  private getLatestNotification(data) {
    const mostRecentDate = new Date(
      Math.max(...data.map(contactHistory => {
          return new Date(contactHistory.contacted_at);
        }
      ))
    );

    // Most recent contact
    return data.filter(e => {
      const date = new Date(e.contacted_at);
      return date.getTime() === mostRecentDate.getTime();
    })[0];
  }

  private getSuccessfulCalls(data) {
    // data.filter(his => console.log(his));
    return data.filter(
      contactHistory => (typeof contactHistory.notifiable !== 'undefined' && contactHistory.notifiable.success ===
        1));
  }

  private formatDateForDisplay(date) {
    const diff  = DateTime.local().diff(DateTime.fromISO(date)).as('milliseconds');
    const d     = Duration.fromMillis(diff);
    const days  = `${Math.floor(d.as('days'))} d`;
    const hours = `${d.hours} hr${d.hours > 1 || d.hours === 0 ? 's' : ''}`;

    return days + ', ' + hours;
  }

  // TODO: save paginate settings to localStorage
  public paginatorChange($event: PageEvent): void {
    this.paginatorConfig.pageIndex = $event.pageIndex;
    this.paginatorConfig.pageSize  = $event.pageSize;
    this.paginatorConfig.length    = $event.length;

    this.caseCreditorListFilter.page     = this.paginatorConfig.pageIndex + 1;
    this.caseCreditorListFilter.per_page = this.paginatorConfig.pageSize;
    this.fetchCaseCreditors();
  }

  public sortData(sort): void {
    this.caseCreditorListFilter.sort_by = sort.active ? sort.active : this.defaultSort.active;
    this.caseCreditorListFilter.order   = sort.direction ? sort.direction : this.defaultSort.direction;

    this.fetchCaseCreditors();
  }

  private clearSelection(): void {
    this.selection.clear();
    this.pageSelected = false;  //  Hide global select
    this.patchSelectAllFilter(0);
    delete this.caseCreditorListFilter.case_creditor_ids;
  }

  private patchSelectAllFilter(state: boolean | 0 | 1): void {
    if (this.creditorCasesListFiltersComponent) {
      this.creditorCasesListFiltersComponent.patchFilter('select_all', state, {emitEvent: false, onlySelf: true});
      this.caseCreditorListFilter.select_all = state;
    } else {
      console.log('Filter component not ready for form pControl patching');
    }
  }

  // Note: Untested TODO: Refactor
  // ==========================================================================================================
  public toggleRow(event: MatCheckboxChange, row): void {
    this.selection.toggle(row);
    if ( ! event.checked) {
      this.pageSelected = false;  //  Hide global select
      this.patchSelectAllFilter(0);
    }
  }

  public globalSelectAll($event): void {
    $event.preventDefault();
    this.patchSelectAllFilter(1);
  }

  public globalClearSelectAll($event): void {
    $event.preventDefault();
    this.clearSelection();
  }

  public togglePageSelect($event) {
    this.pageSelected = $event.checked;
    if ( ! this.pageSelected) {
      this.patchSelectAllFilter(0);
    }
    if (this.allRowsSelected()) {
      this.dataSource.data?.forEach(clientCase => this.selection.deselect(clientCase));
    } else {
      this.dataSource.data?.forEach(clientCase => this.selection.select(clientCase));
    }
  }

  public allRowsSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  private getTableColumns(): Array<string> {
    return ['select',
      'reference_number',
      'product',
      'name',
      'creditor_type',
      'case_creditor_status',
      'payment_status',
      'case_status',
      'original_debt',
      'discount',
      'current_balance',
      'settlement_balance',
      'can_settle',
      'no_of_creditors',
      'days_in_status',
      'amount_paid',
      'actions'];
  }

  public applyFilters(): void {
    this.resetPagination();
    this.resetSort();
    this.fetchCaseCreditors();
  }

  public exportRecords(caseCreditorListFilter: CaseCreditorListFilter): void {
    caseCreditorListFilter.case_creditor_ids = [];
    this.selection.selected.forEach(selectedCase => caseCreditorListFilter.case_creditor_ids.push(selectedCase.id));
    caseCreditorListFilter.expected_count = caseCreditorListFilter.select_all ?
      this.totalResults :
      caseCreditorListFilter.case_creditor_ids.length;
    const requestFilter                   = MainBaseApiService.convertFiltersForRequest(caseCreditorListFilter, 'get');

    this.isLoadingExport++;
    this.subscriptions.push(
      this.caseCreditorService.export(requestFilter).pipe(finalize(() => this.isLoadingExport--)).subscribe(
        result => {
          this.toastr.success(result.message);
          this.clearSelection();
        },
        err => console.error(err)
      )
    );
    this.caseCreditorListFilter.case_creditor_ids = null;
  }

  public isElementRestricted(clientCase: Case): boolean {

    if ( ! clientCase || ! clientCase.status_id) {
      return false;
    }

    const enabledRoles         = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId      = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (
      ! enabledStatusId.includes(clientCase.status_id) ||
      enabledRoles.includes(this.authUser.role_id) ||
      isDepartmentAllowed
    ) {
      return true;
    }
    return false;
  }

  public requestDebtSettlement(caseCreditor: CaseCreditor | any, clientCase: Case): void {
    // if the settlement_balance is not sufficient, show error

    this.configService.getConfigData().subscribe(next => {
      const defaultNegotiationPercent = next.data.find(obj => obj.key === 'ds_reduction_percent').value;

      const creditorFee    = caseCreditor.negotiated_amount ?? 0;
      const negotiationFee = ((caseCreditor.initial_balance - caseCreditor.negotiated_amount) * defaultNegotiationPercent / 100) || 0;
      const debtRequestFee = +creditorFee + +negotiationFee || 0;

      const html     = `
          <table style="text-align: left;">
              <tr>
                  <td>${this.translate.instant('CREDITORS.debt-settlement-request.negotiation-fee')}: ${negotiationFee}</td>
              </tr>
              <tr>
                  <td>${this.translate.instant('CREDITORS.debt-settlement-request.total-quick-win')}: ${debtRequestFee}</td>
              </tr>
          </table>
        `;
      let buttonText = this.translate.instant('CREDITORS.debt-settlement-request.actions.send');
      if (caseCreditor.case_creditor_status.slug === 'ds_settlement_requested') {
        buttonText = this.translate.instant('CREDITORS.debt-settlement-request.actions.accept');
      }

      Swal.fire({
        title:              this.translate.instant('CREDITORS.debt-settlement-request.title') + ': ' + caseCreditor.creditor.name,
        html,
        showCancelButton:   true,
        confirmButtonText:  buttonText,
        confirmButtonColor: '#886ab5',
        cancelButtonText:   this.translate.instant('SHARED.cancel'),
      }).then(res => {
        if (res.isConfirmed) {
          this.isLoading++;
          const data                   = {};
          const status                 = this.caseCreditorStatuses.find(status => status.slug === 'ds_settlement_requested');
          // @ts-ignore
          data.case_creditor_status_id = status.id;
          // @ts-ignore
          data.product_id              = 19;

          if (caseCreditor.case_creditor_status.slug === 'ds_settlement_requested') {
            // @ts-ignore
            data.case_creditor_status_id = status.id;
          }

          this.caseCreditorService.updateCaseCreditor(clientCase.id, caseCreditor.id, data)
            .pipe(finalize(() => this.isLoading--))
            .subscribe(
              value => {
                this.toastr.success(this.translate.instant('CASES.details.change-status-success'));
                this.fetchCaseCreditors();
                Swal.close();
              },
              error => {
                if (error.error?.data?.message) {
                  this.toastr.error(error.error.data.message);
                } else {
                  this.toastr.error(this.translate.instant('CASES.details.change-status-error'));
                }
              });
        }
      });
    });
  }

  private fetchCaseCreditorStatuses(): void {
    this.isLoading++;
    const data = {
      'append[]': ['name'],
      select_all: 1,
    };
    this.caseCreditorStatusService.index(data).pipe(finalize(() => this.isLoading--))
      .subscribe(res => {
        this.caseCreditorStatuses = res.data;
      });
  }

  public openPaymentPlanGenerationModal(additionalData: CaseCreditor, clientCase: Case): void {
    const dialogRef = this.dialog.open(CasePaymentPlanGeneratorComponent, {
      width: '60%',
      data:  {
        case:     clientCase,
        creditor: additionalData
      },
    });
  }

  public verifyCreditor($event, caseCreditorId, creditorType, caseDetails: Case = null): void {
    $event.preventDefault();
    console.log('caseDetails', caseDetails);
    let failure = false;
    caseDetails.contracts.forEach(contract => {
      if (contract.mandate_location == null || contract.pdf_location == null) {
        failure = true;
        Swal.fire({
          title: 'File Problem', // this.translate.instant('CASES.details.verify-creditor-error'),
          // this.translate.instant('CASES.details.verify-creditor-error-message'),
          text:              'Mandate or Contract PDF is not found, please regenerate!',
          icon:              'error',
          confirmButtonText: this.translate.instant('SHARED.close'),
        });
      }
    });
    if (failure) {
      return;
    }
    const caseCreditorIds = caseCreditorId.selected ? caseCreditorId.selected.map(cred => cred.id) : caseCreditorId;
    if ( !! caseDetails.client.signature_verified_at) {
      if (creditorType === 'public_debts') {
        this.additionalInput(caseCreditorIds, creditorType, caseDetails);
      } else {
        this.verifySendMandates(caseCreditorIds, creditorType);
      }
    } else {
      this.verifySignature(caseCreditorIds, creditorType, caseDetails);
    }
  }

  private additionalInput(debtIds, creditorType, caseDetails: Case): void {
    const ids              = debtIds.selected ? debtIds.selected.map(cred => cred.id) : debtIds;
    this.isPublicLoading   = 1;
    this.isPublicLoadingId = ids;

    const dialogRef = this.dialog.open(AdditionalInputComponent, {
      width:  '50%',
      height: '50%',
      data:   {
        case: caseDetails,
        debtIds,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.data.message) {
          ids.map(id => {
            this.markAsVerified(id, creditorType);
          });
          this.isPublicLoading   = 0;
          this.isPublicLoadingId = [];
          if (debtIds.selected) {
            debtIds.clear(); //  Remove selections
          }
        }
      } else {
        this.isPublicLoading   = 0;
        this.isPublicLoadingId = [];
        if (debtIds.selected) {
          debtIds.clear(); //  Remove selections
        }
      }
    });
  }

  public markAsVerified(id, type): void {
    if (type === 'unsecured_creditors') {
      // @ts-ignore
      const item    = this.unsecuredCreditors.data.find(i => i.id === id);
      // @ts-ignore
      item.verified = 1;
    }
    if (type === 'excluded_creditors') {
      // @ts-ignore
      const item    = this.excludedCreditors.data.find(i => i.id === id);
      // @ts-
      item.verified = 1;
    }
  }

  private verifySendMandates(caseCreditorId, creditorType): void {
    const ids = caseCreditorId.selected ? caseCreditorId.selected.map(cred => cred.id) : caseCreditorId;
    this.isLoading++;
    this.isLoadingIds = ids;
    this.creditorService.verifySendMandates(ids[0], ids.length > 1 ? {selected_ids: ids} : null)
      .pipe(finalize(() => {
        this.isLoading--;
        this.isLoadingIds = [];
      }))
      .subscribe(
        value => {
          // @ts-ignore
          value.data.messages.map(message => {
            this.toastr.success(message.message);
          });

          ids.map(id => {
            this.markAsVerified(id, creditorType);
            this.updateStatus(id, creditorType, 'mandate_sent');
          });
          if (caseCreditorId.selected) {
            caseCreditorId.clear(); //  Remove selections
          }
        },
        error => {
          if (caseCreditorId.selected) {
            caseCreditorId.clear(); //  Remove selections
          }
          if (error.error.errors.case.errors && error.error.errors.case.errors.length) {
            this.throwMultipleErrors(error.error.errors.case.errors);
          } else {
            this.toastr.error(this.translate.instant('CASES.details.resend-mandates-error'));
          }
        });
  }

  public updateStatus(id, type, value): void {
    if (type === 'secured_creditors') {
      // @ts-ignore
      const item             = this.securedCreditors.data.find(i => i.id === id);
      // @ts-ignore
      item.response_received = value;
    }
    if (type === 'unsecured_creditors') {
      // @ts-ignore
      const item             = this.unsecuredCreditors.data.find(i => i.id === id);
      // @ts-ignore
      item.response_received = value;
    }
  }

  private throwMultipleErrors(errorsArray: Array<string> | Array<{
    errors: string, id: number, name: string,
    reference_number: string
  }>): void {
    errorsArray.forEach(caseCreditorError => {
      this.toastr.error(caseCreditorError, '',
        {disableTimeOut: true});
    });
  }

  private verifySignature(caseCreditorId, creditorType, clientCase: Case): void {
    const dialogRef = this.dialog.open(VerifySignatureComponent, {
      width:  '50%',
      height: '50%',
      data:   {
        case:           clientCase,
        verifyCreditor: true,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.data.message) {
          if (creditorType === 'public_debts') {
            this.additionalInput(caseCreditorId, creditorType, clientCase);
          } else {
            this.verifySendMandates(caseCreditorId, creditorType);
          }
          // this.updateSignatureVerification.emit(1);
        }
      }
    });
  }

  public sendMandatesRecoveryAgent($event, caseCreditorId): void {
    $event.preventDefault();
    const ids = caseCreditorId.selected ? caseCreditorId.selected.map(cred => cred.id) : caseCreditorId;
    this.isLoading++;
    this.isLoadingIds = ids;
    this.creditorService.sendMandatesRecoveryAgent(ids[0], ids.length > 1 ? {selected_ids: ids} : null)
      .pipe(finalize(() => {
        this.isLoading--;
        this.isLoadingIds = [];
      }))
      .subscribe(
        value => {
          // @ts-ignore
          value.data.messages.map(message => {
            this.toastr.success(message.message);
          });
          if (caseCreditorId.selected) {
            caseCreditorId.clear(); //  Remove selections
          }
        },
        error => {
          if (caseCreditorId.selected) {
            caseCreditorId.clear(); //  Remove selections
          }
          if (error.error.errors.case.errors && error.error.errors.case.errors.length) {
            this.throwMultipleErrors(error.error.errors.case.errors);
          } else {
            this.toastr.error(this.translate.instant('CASES.details.email-sent-error'));
          }
        });
  }

  public sendProposalAndAccord(caseCreditorIds: Array<number>, selection: SelectionModel<CaseCreditorPivot> = null, clientCase: Case): void {
    this.isLoading++;
    this.isLoadingIds = caseCreditorIds;
    this.creditorService.sendProposalAndAcoord(clientCase.id, {selected_ids: caseCreditorIds})
      .pipe(finalize(() => {
        this.isLoading--;
        this.isLoadingIds = [];
      }))
      .subscribe(
        value => {
          this.toastr.success(this.translate.instant('CASES.details.send-proposal-accord-success'));
          if (selection.selected) {
            selection.clear();
          }
        },
        error => {
          if (selection.selected) {
            selection.clear();
          }
          if (error.error.errors.case.errors && error.error.errors.case.errors.length) {
            this.throwMultipleErrors(error.error.errors.case.errors);
          } else {
            this.toastr.error(this.translate.instant('CASES.details.send-proposal-accord-error'));
          }
        });
  }

  public creditorDetails(creditor: Creditor, $event): void {
    $event.preventDefault();
    this.dialog.open(CaseCreditorInfoComponent, {
      width: 'fit-content',
      data:  creditor,
    });
  }

  public openCalculationModal(caseCreditor: CaseCreditor, $event: Event): void {
    $event.preventDefault();
    this.dialog.open(CalculationModalComponent, {
      width: 'fit-content',
      data:  {
        caseCreditor,
        settlementBalance: (caseCreditor.case?.settlement_balance || 0),
      }
    });
  }

  public changeStatus($event: MouseEvent, selection = null, type: string, element: CaseCreditor): void {
    $event.preventDefault();

    const ids = [element.id];

    const dialogRef = this.dialog.open(ChangeCreditorStatusComponent, {
      width:  '30%',
      height: 'fit-content',
      data:   {
        case: element.case,
        ids,
        type,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (selection.selected) {
        selection.clear(); //  Remove selections
      }
      if (result) {
        ids.map(id => {
          this.updateStatus(id, type, result);
        });
      }
      this.fetchCaseCreditors();
    });
  }

  public sendExtrajudicialClaim(selectedCaseCreditors, clientCase: Case): void {
    const selectedIds = selectedCaseCreditors.selected ?
      selectedCaseCreditors.selected.map(cred => cred.id) :
      selectedCaseCreditors;
    this.isLoading++;
    this.isLoadingIds = selectedIds;

    this.creditorService.sendExtrajudicialClaim(clientCase.id, {selected_ids: selectedIds})
      .pipe(finalize(() => {
        this.isLoading--;
        this.isLoadingIds = [];
        if (selectedCaseCreditors.selected) {
          selectedCaseCreditors.clear(); //  Remove selections
        }
      }))
      .subscribe(
        value => {
          this.toastr.success(this.translate.instant('SHARED.success'));
        },
        error => {
          if (error) {
            this.throwMultipleErrors(error.error.errors.case.errors);
          } else {
            this.toastr.error(this.translate.instant('SHARED.error'));
          }
        });
  }

  public sendExtrajudicialClaims(caseCreditorListFilter: CaseCreditorListFilter): void {

    const relations = ['packager_status', 'case_invoice_status', 'call_status', 'packager', 'affiliate', 'product', 'lead.affiliate',
      'payment_status', 'status', 'latest_case_status_log', 'oldest_case_pending_document', 'creditors', 'doc_status', 'case_status_logs', 'expense', 'distribution'];

    this.filters = {
      is_from_case_creditor:    1,
      amount_paid_greater_then: caseCreditorListFilter.amount_paid,
      case_creditor_statuses:   this.caseCreditorListFilter.case_creditor_statuses,
      all_active:               1,
    };
    // turn creditor id into array
    if (this.caseCreditorListFilter.creditors) {
      const creditor = [];
      creditor.push(this.caseCreditorListFilter.creditors);
      this.caseCreditorListFilter.creditors = creditor;
    }

    caseCreditorListFilter.cases = [];
    this.selection.selected.forEach(selectedCase => caseCreditorListFilter.cases.push(selectedCase.id));
    caseCreditorListFilter.expected_count = caseCreditorListFilter.select_all ? this.totalResults : caseCreditorListFilter.cases.length;

    Object.assign(this.filters, this.caseCreditorListFilter);
    const requestData = MainBaseApiService.convertFiltersForRequest(
      {...this.filters, with: relations}, 'get'
    );
    this.isLoading++;
    this.subscriptions.push(
      this.caseCreditorStatusService.sendExtrajudicialClaims(requestData).pipe(finalize(() => this.isLoading--)).subscribe(
        result => {
          this.toastr.success(result.message);
          this.clearSelection();
        },
        err => console.error(err)
      )
    );
  }

}
